// src/components/admin/sections/AdminTeam.tsx
import React, { useState, useEffect } from 'react';
import apiElCristo, { TeamMember, TeamData } from '../../../service/api_elcristo';

const AdminTeam: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const [teamData, setTeamData] = useState<TeamData>({
    subtitle: '',
    title: '',
    teamMembers: []
  });

  const [formData, setFormData] = useState<TeamMember>({
    name: '',
    role: '',
    image: '',
    socialLinks: {
      facebook: '',
      twitter: '',
      youtube: '',
      linkedin: ''
    }
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await apiElCristo.getTeamData();
      setTeamData(data);
    } catch (error) {
      console.error('Error loading team:', error);
      setMessage({ type: 'error', text: 'Error al cargar el equipo' });
    } finally {
      setLoading(false);
    }
  };

  const handleSectionChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'subtitle' | 'title') => {
    const value = e.target.value;
    setTeamData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOpenModal = (member?: TeamMember, index?: number) => {
    if (member && index !== undefined) {
      setEditingMember(member);
      setEditingIndex(index);
      setFormData(member);
    } else {
      setEditingMember(null);
      setEditingIndex(null);
      setFormData({
        name: '',
        role: '',
        image: '',
        socialLinks: {
          facebook: '',
          twitter: '',
          youtube: '',
          linkedin: ''
        }
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingMember(null);
    setEditingIndex(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name.startsWith('social.')) {
      const socialField = name.split('.')[1] as keyof typeof formData.socialLinks;
      setFormData(prev => ({
        ...prev,
        socialLinks: { ...prev.socialLinks, [socialField]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSaveMember = () => {
    if (editingIndex !== null) {
      // Actualizar miembro existente
      const updated = [...teamData.teamMembers];
      updated[editingIndex] = formData;
      setTeamData(prev => ({ ...prev, teamMembers: updated }));
    } else {
      // Agregar nuevo miembro
      setTeamData(prev => ({
        ...prev,
        teamMembers: [...prev.teamMembers, formData]
      }));
    }
    handleCloseModal();
  };

  const handleDeleteMember = (index: number) => {
    if (!window.confirm('¿Estás seguro de eliminar este miembro del equipo?')) {
      return;
    }

    setTeamData(prev => ({
      ...prev,
      teamMembers: prev.teamMembers.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      await apiElCristo.updateTeamData(teamData);
      setMessage({ type: 'success', text: '✅ Equipo actualizado correctamente' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Error saving team:', error);
      setMessage({ type: 'error', text: '❌ Error al guardar: ' + error.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="elcristo-admin-content">
        <div className="elcristo-loading-spinner">
          <div className="elcristo-spinner"></div>
          <p>Cargando equipo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="elcristo-admin-content">
      <div className="elcristo-content-header">
        <div>
          <h1><i className="fas fa-users"></i> Gestión del Equipo Docente</h1>
          <p>Administrar profesores y personal del colegio</p>
        </div>
        <button className="elcristo-btn-primary" onClick={() => handleOpenModal()}>
          <i className="fas fa-user-plus"></i>
          Nuevo Miembro
        </button>
      </div>

      {message && (
        <div className={`elcristo-alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Section Settings */}
        <div className="elcristo-admin-form">
          <div className="elcristo-form-section">
            <h3><i className="fas fa-cog"></i> Configuración de Sección</h3>
            
            <div className="elcristo-form-row">
              <div className="elcristo-form-group">
                <label htmlFor="subtitle">Subtítulo de Sección</label>
                <input
                  type="text"
                  id="subtitle"
                  value={teamData.subtitle || ''}
                  onChange={(e) => handleSectionChange(e, 'subtitle')}
                  placeholder="Ej: Nuestro Equipo"
                />
              </div>

              <div className="elcristo-form-group">
                <label htmlFor="title">Título de Sección</label>
                <input
                  type="text"
                  id="title"
                  value={teamData.title || ''}
                  onChange={(e) => handleSectionChange(e, 'title')}
                  placeholder="Ej: Equipo Docente"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Team Members Grid */}
        <div className="elcristo-team-section">
          <div className="elcristo-section-header">
            <h3><i className="fas fa-users"></i> Miembros del Equipo ({teamData.teamMembers.length})</h3>
          </div>

          <div className="elcristo-team-grid">
            {teamData.teamMembers.map((member, index) => (
              <div key={index} className="elcristo-team-card">
                <div className="elcristo-team-image">
                  <img src={member.image} alt={member.name} />
                  <div className="elcristo-team-overlay">
                    <button
                      type="button"
                      className="elcristo-btn-icon-white"
                      onClick={() => handleOpenModal(member, index)}
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      type="button"
                      className="elcristo-btn-icon-white"
                      onClick={() => handleDeleteMember(index)}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
                <div className="elcristo-team-info">
                  <h4>{member.name}</h4>
                  <p className="elcristo-team-position">{member.role}</p>
                  {member.socialLinks && (
                    <div className="elcristo-team-social">
                      {member.socialLinks.facebook && (
                        <a href={member.socialLinks.facebook} target="_blank" rel="noopener noreferrer">
                          <i className="fab fa-facebook"></i>
                        </a>
                      )}
                      {member.socialLinks.twitter && (
                        <a href={member.socialLinks.twitter} target="_blank" rel="noopener noreferrer">
                          <i className="fab fa-twitter"></i>
                        </a>
                      )}
                      {member.socialLinks.youtube && (
                        <a href={member.socialLinks.youtube} target="_blank" rel="noopener noreferrer">
                          <i className="fab fa-youtube"></i>
                        </a>
                      )}
                      {member.socialLinks.linkedin && (
                        <a href={member.socialLinks.linkedin} target="_blank" rel="noopener noreferrer">
                          <i className="fab fa-linkedin"></i>
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {teamData.teamMembers.length === 0 && (
              <div className="elcristo-empty-state">
                <i className="fas fa-users"></i>
                <p>No hay miembros del equipo</p>
                <button
                  type="button"
                  className="elcristo-btn-primary"
                  onClick={() => handleOpenModal()}
                >
                  <i className="fas fa-user-plus"></i>
                  Agregar Primer Miembro
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="elcristo-form-actions">
          <button type="button" className="elcristo-btn-secondary" onClick={loadData} disabled={saving}>
            <i className="fas fa-undo"></i>
            Recargar
          </button>
          
          <button type="submit" className="elcristo-btn-primary" disabled={saving}>
            {saving ? (
              <>
                <span className="elcristo-spinner-small"></span>
                Guardando...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Guardar Cambios
              </>
            )}
          </button>
        </div>
      </form>

      {/* Modal */}
      {showModal && (
        <div className="elcristo-modal-overlay" onClick={handleCloseModal}>
          <div className="elcristo-modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="elcristo-modal-header">
              <h2>
                <i className="fas fa-user"></i>
                {editingMember ? 'Editar Miembro' : 'Nuevo Miembro'}
              </h2>
              <button className="elcristo-modal-close" onClick={handleCloseModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="elcristo-modal-body">
              <div className="elcristo-form-row">
                <div className="elcristo-form-group">
                  <label>Nombre Completo *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Ej: María González"
                    required
                  />
                </div>

                <div className="elcristo-form-group">
                  <label>Cargo *</label>
                  <input
                    type="text"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    placeholder="Ej: Directora, Profesor de Matemáticas"
                    required
                  />
                </div>
              </div>

              <div className="elcristo-form-group">
                <label>Imagen URL *</label>
                <input
                  type="text"
                  name="image"
                  value={formData.image}
                  onChange={handleChange}
                  placeholder="/assets/kidsa/assets/img/team/01.jpg"
                  required
                />
                {formData.image && (
                  <div className="elcristo-image-preview">
                    <img src={formData.image} alt="Preview" />
                  </div>
                )}
              </div>

              <div className="elcristo-form-section">
                <h4><i className="fab fa-facebook"></i> Redes Sociales</h4>
                <p className="elcristo-form-hint">Opcional - Deja en blanco las redes que no uses</p>
                
                <div className="elcristo-form-row">
                  <div className="elcristo-form-group">
                    <label>
                      <i className="fab fa-facebook"></i> Facebook
                    </label>
                    <input
                      type="text"
                      name="social.facebook"
                      value={formData.socialLinks?.facebook || ''}
                      onChange={handleChange}
                      placeholder="https://facebook.com/usuario"
                    />
                  </div>

                  <div className="elcristo-form-group">
                    <label>
                      <i className="fab fa-twitter"></i> Twitter
                    </label>
                    <input
                      type="text"
                      name="social.twitter"
                      value={formData.socialLinks?.twitter || ''}
                      onChange={handleChange}
                      placeholder="https://twitter.com/usuario"
                    />
                  </div>
                </div>

                <div className="elcristo-form-row">
                  <div className="elcristo-form-group">
                    <label>
                      <i className="fab fa-youtube"></i> YouTube
                    </label>
                    <input
                      type="text"
                      name="social.youtube"
                      value={formData.socialLinks?.youtube || ''}
                      onChange={handleChange}
                      placeholder="https://youtube.com/@usuario"
                    />
                  </div>

                  <div className="elcristo-form-group">
                    <label>
                      <i className="fab fa-linkedin"></i> LinkedIn
                    </label>
                    <input
                      type="text"
                      name="social.linkedin"
                      value={formData.socialLinks?.linkedin || ''}
                      onChange={handleChange}
                      placeholder="https://linkedin.com/in/usuario"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="elcristo-modal-footer">
              <button type="button" className="elcristo-btn-secondary" onClick={handleCloseModal}>
                <i className="fas fa-times"></i>
                Cancelar
              </button>
              <button type="button" className="elcristo-btn-primary" onClick={handleSaveMember}>
                <i className="fas fa-save"></i>
                {editingMember ? 'Actualizar' : 'Agregar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTeam;